import React, { useState, useLayoutEffect, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchActionitemsAction } from 'Shared/actions/meetingItems';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import Spinner from 'Shared/components/Spinner';
import { clearSelectedUuids } from 'Shared/actions/selectedUuids';
import { getUniqAssignedUsers } from 'Shared/selectors/meetingItems';
import { ActionItemsContext } from 'Shared/api/types';
import { sortUsersByName } from 'Shared/utils/sorting';
import { UserRole } from 'Shared/constants/types';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';

import ActionItemsEmptyState from './ActionItemsEmptyState/ActionItemsEmptyState';
import { EmptyStateVariant } from './ActionItemsEmptyState/constants';
import TeamUserOpenItems from './TeamUserOpenItems/TeamUserOpenItems';
import TeamUserCompletedItems from './TeamUserCompletedItems/TeamUserCompletedItems';

const LIMIT_FOR_COMPLETED_ITEMS = 3;

const TeamActionItems = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isViewingComplete, setIsViewingComplete] = useState(false);
  const [countItemsByUserId, setCountItemsByUserId] = useState({});
  const users = useDeepEqualSelector(getUniqAssignedUsers());
  const sortedUsers = [
    ...sortUsersByName(users.filter(({ role }) => role === UserRole.manager)),
    ...sortUsersByName(users.filter(({ role }) => role === UserRole.fullMember)),
  ];
  const usersPersonIds = users.map(({ personId }) => personId);
  const actionItemsCount = useSelector(
    state =>
      state.meetingItems.filter(
        ({ assignmentPersonId, completed }) =>
          usersPersonIds.includes(assignmentPersonId) && completed === isViewingComplete
      ).length
  );

  useLayoutEffect(() => {
    async function fetchActionItems() {
      setIsLoading(true);
      const { countItemsByUserId: countItems } = await dispatch(
        fetchActionitemsAction({
          context: isViewingComplete ? ActionItemsContext.teamWithPagination : ActionItemsContext.team,
          status: isViewingComplete ? 'completed' : 'not_completed',
          limit: LIMIT_FOR_COMPLETED_ITEMS,
        })
      );
      setCountItemsByUserId(countItems);
      setIsLoading(false);
    }
    dispatch(clearSelectedUuids());
    fetchActionItems();
  }, [isViewingComplete]);

  useEffect(
    () => () => {
      dispatch(clearSelectedUuids());
      dispatch({ type: 'CLEAR_MEETING_ITEMS' });
    },
    []
  );

  const showEmptyState = actionItemsCount === 0;

  const emptyStateVariant = isViewingComplete
    ? EmptyStateVariant.actionItemsComplete
    : EmptyStateVariant.actionItemsOpen;

  return (
    <div className="TeamGoals">
      <div className="d-flex align-items-center">
        <span className="mr-2">Show: </span>
        <WpDropdown>
          <WpDropdownToggle>
            <WpButton color="primary" variant="tertiary" size="sm">
              {isViewingComplete ? 'Completed Action Items' : 'Open Action Items'}
              <WpIcon name="chevron-down" />
            </WpButton>
          </WpDropdownToggle>
          <WpDropdownMenu>
            <WpDropdownMenuItem onClick={() => setIsViewingComplete(false)}>Open Action Items</WpDropdownMenuItem>
            <WpDropdownMenuItem onClick={() => setIsViewingComplete(true)}>Completed Action Items</WpDropdownMenuItem>
          </WpDropdownMenu>
        </WpDropdown>
      </div>
      {isLoading ? (
        <Spinner activeIf />
      ) : (
        <>
          {!showEmptyState &&
            isViewingComplete &&
            sortedUsers.map(user => (
              <TeamUserCompletedItems key={user.id} user={user} countItems={countItemsByUserId[user.id]} />
            ))}
          {!showEmptyState &&
            !isViewingComplete &&
            sortedUsers.map(user => <TeamUserOpenItems key={user.id} user={user} />)}
          {showEmptyState && <ActionItemsEmptyState variant={emptyStateVariant} />}
        </>
      )}
    </div>
  );
};

export default memo(TeamActionItems);
